import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './LiveDonationBoard.css';

const LiveDonationBoard = () => {
    const navigate = useNavigate();
    const [donations, setDonations] = useState([]);
    const [announcedCount, setAnnouncedCount] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const intervalRef = useRef(null);

    useEffect(() => {
        // Check if user is admin
        const loginData = localStorage.getItem('login');
        if (loginData) {
            try {
                const user = JSON.parse(loginData);
                if (user.role !== 'admin') {
                    toast.error('Access denied. Admin only.');
                    navigate('/');
                    return;
                }
            } catch (error) {
                navigate('/login');
                return;
            }
        } else {
            navigate('/login');
            return;
        }

        // Fetch donations immediately
        fetchDonations();

        // Auto-refresh every 3 seconds
        intervalRef.current = setInterval(() => {
            fetchDonations();
        }, 60000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [navigate]);

    const fetchDonations = async () => {
        try {
            const response = await fetch('https://hospital.rozeetechnist.com/hospital-api/getLiveDonations.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    date: new Date().toISOString().split('T')[0] // Today's date
                })
            });

            const result = await response.json();

            if (result.status) {
                setDonations(result.data || []);

                // Use total amount of ALL donations today (including announced) from API
                if (result.total_donations_amount_today !== undefined) {
                    setTotalAmount(result.total_donations_amount_today);
                } else {
                    // Fallback: Calculate from pending donations if API doesn't return total
                    const total = result.data.reduce((sum, donation) => {
                        return sum + parseFloat(donation.donation_amount.replace(/,/g, ''));
                    }, 0);
                    setTotalAmount(total);
                }

                setIsLoading(false);
            } else {
                setDonations([]);
                setIsLoading(false);
            }
        } catch (error) {
            console.error('Error fetching donations:', error);
            setIsLoading(false);
        }
    };

    const markAsAnnounced = async (donationId, index) => {
        const loginData = localStorage.getItem('login');
        let announcedBy = null;

        if (loginData) {
            try {
                const user = JSON.parse(loginData);
                announcedBy = user.id;
            } catch (error) {
                console.error('Error parsing login data:', error);
            }
        }

        try {
            const response = await fetch('https://hospital.rozeetechnist.com/hospital-api/markDonationAnnounced.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: donationId,
                    announced_by: announcedBy
                })
            });

            const result = await response.json();

            if (result.status) {
                // Remove from list with animation
                setDonations(prev => prev.filter((_, i) => i !== index));
                setAnnouncedCount(prev => prev + 1);

                // Refresh donations to get updated total from API
                fetchDonations();
            } else {
                toast.error(result.message || 'Failed to mark as announced');
            }
        } catch (error) {
            console.error('Error marking as announced:', error);
            toast.error('An error occurred');
        }
    };

    const formatAmount = (amount) => {
        return parseFloat(amount.toString().replace(/,/g, '')).toLocaleString('en-IN', {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
        });
    };

    return (
        <div className="live-donation-board">
            <div className="board-header">
                <div className="header-content">
                    <h1 className="board-title">સુમરા હોસ્પિટલ</h1>
                    <h2 className="board-subtitle">Live Donation Board</h2>
                    <div className="stats-bar">
                        <div className="stat-item">
                            <span className="stat-label">Pending:</span>
                            <span className="stat-value">{donations.length}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">Announced:</span>
                            <span className="stat-value">{announcedCount}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">Total Amount:</span>
                            <span className="stat-value">₹{formatAmount(totalAmount)}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="board-content">
                {isLoading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading donations...</p>
                    </div>
                ) : donations.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">🎉</div>
                        <h3>All Donations Announced!</h3>
                        <p>No pending donations to announce</p>
                    </div>
                ) : (
                    <div className="donations-grid">
                        {donations.map((donation, index) => (
                            <div
                                key={donation.id}
                                className="donation-card"
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                <div className="card-header">
                                    <span className="receipt-number">Receipt: {donation.receipt_no}</span>
                                    <span className="donation-type">{donation.type_of_donation}</span>
                                </div>
                                <div className="card-body">
                                    <h3 className="donor-name">{donation.name}</h3>
                                    <div className="donation-details">
                                        <div className="detail-item">
                                            <span className="detail-label">City:</span>
                                            <span className="detail-value">{donation.city_name}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Amount:</span>
                                            <span className="detail-value amount">₹{donation.donation_amount}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-footer">
                                    <button
                                        className="announce-btn"
                                        onClick={() => markAsAnnounced(donation.id, index)}
                                    >
                                        ✓ Mark as Announced
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="board-footer">
                <p>Last updated: {new Date().toLocaleTimeString()}</p>
                <button className="back-btn" onClick={() => navigate('/')}>
                    ← Back to Dashboard
                </button>
            </div>
        </div>
    );
};

export default LiveDonationBoard;
